import { useEffect, useState } from "react"
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa6";
import axios from "axios"
import { Link, useNavigate } from "react-router-dom"
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {Helmet} from "react-helmet";

const Register = () => {
    const navigate = useNavigate()
    const [showOtp, setShowOtp] = useState(false)
    const [loading, setLoading] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)
    const { t } = useTranslation()

    // Function to start the timer and store timestamp
    const startTimer = () => {
        const now = Date.now()
        const expireTime = now + 60 * 1000 // 1-minute expiry
        localStorage.setItem("otp_timer", expireTime.toString())
        setTimeLeft(60)
    }

    // Effect to check the timer on mount
    useEffect(() => {
        const storedExpireTime = localStorage.getItem("otp_timer")
        if (storedExpireTime) {
            const remainingTime = Math.floor((parseInt(storedExpireTime) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setTimeLeft(remainingTime)
            }
        }

        // Timer countdown
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    localStorage.removeItem("otp_timer")
                    return 0
                }
                return prev - 1
            });
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const generateOtp = async (values:any) => {
        if (values.email.trim() === "") return toast.error(t('register.email_required'))
        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`, {
            email: values.email.trim(),
        }).then( res => {
            setLoading(false)
            console.log("res", res)
            // If user not exist, would be new user. So we will be also need to ask user to fill in all the info that we needed
            // show otp column
            setShowOtp(true)
            startTimer() // Start timer after OTP request
        }).catch( err => {
            setLoading(false)
            console.log("err", err)
            if (err.response.data && err.response.data.message) {
                toast.error(err.response.data.message)
            } else {
                toast.error(t("server_error"))
            }
        })
    };

    const checkExist = async (values:any) => {
        if (values.email.trim() === "" || values.otp.trim() === "") return toast.error(t('register.email_otp_required'))
        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/exist`, {
            "email": values.email.trim(),
            "otp": values.otp.trim()
        }, {headers: {'Content-Type': 'application/json', "Accept":"application/json"}, withCredentials: true }).then( res => {
            setLoading(false)
            console.log("res check exist", res)
            if (res.data.is_exist === false) {
                navigate(`/register-new?email=${values.email}`)
            } else {
                navigate(`/register-exist?email=${values.email}`)
            }
        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            if (err.response) {
                // If the error has a response, check if it contains data and an error message
                if (err.response.data && err.response.data.error) {
                    toast.error(err.response.data.error)
                } else {
                    toast.error("An error occurred. Please try again.");
                }
            } else if (err.request) {
                // Request was made but no response was received (e.g., network issue)
                toast.error("No response from server. Please check your internet connection.");
            } else {
                // Other errors (e.g., code issues)
                toast.error("Something went wrong. Please try again.");
            }
        })
    }

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(t("validation.invalid_email"))
            .required(t("validation.required")),
        otp: Yup.string().when("showOtp", {
            is: true,
            then: (schema) => schema.required(t("validation.required")),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    return (
        <>
        <Helmet>
            <title>Sign Up for MyKoku | Free Smart School Platform Malaysia</title>
            <meta name="description" content="Create your free MyKoku account today. Used by Malaysian schools to manage attendance, learning & payment. SK, SMK, SJKC & SMJK supported." />
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="pt-4 px-4 pb-12 h-screen">
            <div className="mb-3">
                <div className="px-0">
                    <img src={process.env.PUBLIC_URL + "/assets/img/koku.png"} className="w-20" alt="" />
                    <h3 className="text-2xl font-bold text-black mt-2">{t('register.register_parent_account')}</h3>
                </div>
            </div>

            <div className="px-0">
                <Formik
                    initialValues={{ email: "", otp: "" }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                    showOtp ? checkExist(values) : generateOtp(values);
                }}>
                    {({ isSubmitting, values }) => (
                    <Form className="px-2 py-4 ">
                        <div className="mb-7">
                            <label htmlFor="emailAddress" className="text-sm font-medium text-black block mb-2">
                                {t("register.email")}
                            </label>
                            <Field
                                type="text"
                                name="email"
                                id="emailAddress"
                                placeholder={t("register.enter_email_address")}
                                disabled={showOtp}
                                className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2 disabled:opacity-60"
                            />
                            <ErrorMessage name="email" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>

                        {/* OTP field */}
                        {showOtp && (
                        <div className="mb-7">
                            <label htmlFor="otp" className="text-sm font-medium text-black block mb-2">{t("register.otp")}</label>
                            <Field
                                type="text"
                                name="otp"
                                placeholder={t("register.enter_otp")}
                                className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                            />
                            <ErrorMessage name="otp" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                        )}

                        <div className="mb-6 text-sm text-gray-500">
                            {t("register.already_have_account")}{" "}
                            <Link to="/login" className="text-theme">{t("register.sign_in_now")}</Link>
                        </div>

                        <button
                            type="submit"
                            className="text-white w-full py-[12px] bg-theme rounded-3xl flex items-center justify-center disabled:bg-gray-300"
                            disabled={(!showOtp && loading) || (!showOtp && timeLeft > 0)}
                        >
                        {loading ? (
                            <FaSpinner className="animate-spin" />
                        ) : (
                            showOtp ? t('register.verify_otp') : timeLeft > 0 ? `Resend OTP (${timeLeft}s)` : t("register.send_otp")
                        )}
                        </button>

                        <div className="grid w-1/2 mt-4 px-2 float-right">
                        <select
                            className="px-2 py-1 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                            onChange={(e) => {
                            i18next.changeLanguage(e.target.value);
                        }}>
                            <option value="-1" disabled selected>-- Language --</option>
                            <option value="en">English</option>
                            <option value="zh">Mandarin</option>
                            <option value="ms">Bahasa</option>
                        </select>
                        </div>
                    </Form>
                    )}
                </Formik>
            </div>
        </div>
        </>
    )
}

export default Register